import { motion } from 'framer-motion'
import { GALLERY_STAGES, LINKS } from '../config'

function PixelImagePair({ img1, img2, title, accent }) {
  return (
    <div className="relative mx-auto h-[300px] w-[260px] sm:h-[340px] sm:w-[300px]">
      {[img1, img2].map((src, i) => (
        <motion.a
          key={src}
          href={LINKS.linkedin}
          target="_blank"
          rel="noreferrer"
          data-testid={`gallery-photo-${i === 0 ? 'a' : 'b'}`}
          whileHover={{ y: -10, scale: 1.04 }}
          whileTap={{ y: -6, scale: 1.02 }}
          transition={{ type: 'spring', stiffness: 300, damping: 18 }}
          className={`absolute block overflow-hidden bg-[var(--gallery-bg2)] ${
            i === 0
              ? 'left-0 top-0 z-10 h-[68%] w-[70%]'
              : 'bottom-0 right-0 z-20 h-[68%] w-[70%]'
          }`}
          style={{ border: `3px solid ${accent}`, boxShadow: '6px 6px 0 rgba(0,0,0,0.55)' }}
        >
          <img
            src={src}
            alt={`${title} — photo ${i + 1}`}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover"
          />
        </motion.a>
      ))}
    </div>
  )
}

function StopMarker({ accent }) {
  return (
    <div
      className="relative hidden h-4 w-4 shrink-0 rotate-45 md:block"
      style={{ background: accent, boxShadow: '0 0 0 4px var(--gallery-bg), 0 0 14px ' + accent }}
    />
  )
}

export default function Gallery() {
  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="mc-checker relative overflow-hidden px-6 py-24 text-[var(--gallery-ink)] md:px-12 md:py-32"
    >
      <div className="mx-auto max-w-6xl">
        <div className="text-center">
          <span className="font-pixel text-[10px] leading-none text-[var(--gallery-gold)]">
            &lt; LEVEL SELECT &gt;
          </span>
          <h2 className="font-pixel mt-5 text-xl leading-relaxed sm:text-2xl md:text-3xl">
            THE GALLERY
          </h2>
          <p className="mx-auto mt-5 max-w-md text-sm text-[var(--gallery-ink)]/60">
            Three moments, in-game. Click a photo to jump to LinkedIn.
          </p>
        </div>

        <div className="relative mt-20">
          <div
            className="absolute left-1/2 top-0 hidden h-full w-[3px] -translate-x-1/2 md:block"
            style={{
              background:
                'repeating-linear-gradient(to bottom, var(--gallery-gold) 0, var(--gallery-gold) 8px, transparent 8px, transparent 18px)',
              opacity: 0.35,
            }}
          />

          <div className="flex flex-col gap-20 md:gap-16">
            {GALLERY_STAGES.map((s, i) => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.25 }}
                transition={{ duration: 0.6, delay: i * 0.05 }}
                data-testid={`gallery-stage-${i + 1}`}
                className={`relative flex flex-col items-center gap-8 md:flex-row md:gap-10 ${
                  s.imageSide === 'left' ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                <div className="flex-1">
                  <PixelImagePair img1={s.img1} img2={s.img2} title={s.title} accent={s.accent} />
                </div>

                <StopMarker accent={s.accent} />

                <div className="flex-1 text-center md:text-left">
                  <span className="font-pixel inline-block text-[9px]" style={{ color: s.accent }}>
                    {s.tag}
                  </span>
                  <h3 className="font-display mt-3 text-2xl font-semibold md:text-3xl">{s.title}</h3>
                  <p className="mt-1 text-sm font-medium text-[var(--gallery-ink)]/60">{s.org}</p>
                  <p className="mx-auto mt-4 max-w-sm text-sm leading-relaxed text-[var(--gallery-ink)]/75 md:mx-0">
                    {s.caption}
                  </p>
                  <a
                    href={LINKS.linkedin}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`gallery-link-${i + 1}`}
                    className="font-pixel mt-5 inline-block text-[9px]"
                    style={{ color: s.accent }}
                  >
                    VIEW POST →
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
